import { Suspense, useEffect, useRef } from "react"
import { useLocation } from "react-router"
import { DrawerWrapper } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { useLocationBlocker } from "@/hooks/useLocationBlocker"
import { useNavigationBlocker } from "@/hooks/useNavigationBlocker"
import { DuplicateProfiles } from "@/modules/patients/detail/duplicate-profiles/duplicate-profiles"
import { EpisodeOfCareActionsContainer } from "@/modules/patients/detail/episode-of-care/episode-of-care-actions-container"
import { EpisodeOfCareNavigation } from "@/modules/patients/detail/episode-of-care/episode-of-care-nav"
import { HealthInformationCard } from "@/modules/patients/detail/health-information"
import { PREVENTION_ASSESSMENT_TELECONSULTATION_DRAWER } from "@/modules/patients/detail/health-information/condition/prevention-programs"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { ProgressNotesFeed } from "@/modules/patients/detail/progress-notes-feed"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useHandleAppointmentParam } from "@/modules/patients/hooks/useHandleAppointmentParam"
import { useHandleConclude } from "@/modules/patients/hooks/useHandleConclude"
import { usePatientEpisodesOfCareData } from "@/modules/patients/hooks/usePatientEpisodesOfCareData"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { usePatientAttachmentSubscription } from "@/modules/patients/services/usePatientAttachmentSubscription"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { getIsDirty, useDrawerStore } from "@/stores/useDrawerStore"

const PatientDetail = () => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const location = useLocation()
  const handleConclude = useHandleConclude()
  const theFirstValidation = useRef(false)

  useHandleAppointmentParam()

  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  useNavigationBlocker(getIsDirty)

  const blocker = useLocationBlocker()

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (useDrawerStore.getState().DrawerContent && useDrawerStore.getState().isDirty) {
        confirm({
          onConfirm: () => {
            if (useDrawerStore.getState().onDrawerClose) {
              useDrawerStore.getState().onDrawerClose?.()
            } else {
              useDrawerStore.getState().closeDrawer()
            }
            useDrawerStore.getState().setDirty(false)
            blocker.proceed()
            cancel()
          },
        })
      } else {
        useDrawerStore.getState().closeDrawerWithExclude([PREVENTION_ASSESSMENT_TELECONSULTATION_DRAWER])
        blocker.proceed()
      }
    }
  }, [blocker, cancel, confirm])

  useEffect(() => {
    if (!theFirstValidation.current && handleConclude && location.state && location.state.fromConclude) {
      theFirstValidation.current = true
      handleConclude({ onValid: () => {} })
    }
  }, [handleConclude, location])

  return (
    <main className="@container grid grid-rows-1">
      <div className="@xxl:grid-cols-[minmax(0,1fr)_auto] @xxl:grid-rows-1 grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto]">
        <div className="flex overflow-auto">
          <EpisodeOfCareWrapper />
          <div className="grid max-w-124 min-w-0 shrink-0 basis-[30%] grid-rows-1 overflow-hidden">
            <ScrollWrapper className="h-full">
              <HealthInformationCard country={countryCode} patientId={patientId} />
            </ScrollWrapper>
          </div>
        </div>
        <Suspense fallback={null}>
          <DrawerWrapper
            containerId="patient-drawer"
            className="@xxl:[&>div]:h-full @xxl:[&>div]:w-125 [&>div]:h-125 [&>div]:w-full"
          />
        </Suspense>
        <EpisodeOfCareActionsContainer />
      </div>
    </main>
  )
}

const EpisodeOfCareWrapper = () => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const isEditMode = useEditMode()
  const appointmentId = useAppointmentParam()
  const patientEpisodesOfCareData = usePatientEpisodesOfCareData()
  usePatientAttachmentSubscription(appointmentId)

  return (
    <>
      <div className="@xxl:block basis-[15%] @xs:hidden">
        <EpisodeOfCareNavigation
          patientEpisodesOfCareRef={patientEpisodesOfCareData}
          appointmentId={appointmentId}
          patientId={patientId}
          isEditMode={isEditMode}
          isDropdown={false}
        />
      </div>
      <ScrollWrapper containerClassName="min-w-0" className="h-full">
        <Suspense fallback={null}>
          <div className="mx-auto w-full max-w-186 px-4 pt-4">
            <DuplicateProfiles />
          </div>
        </Suspense>
        <ProgressNotesFeed
          country={countryCode}
          patientId={patientId}
          appointmentId={appointmentId}
          patientEpisodesOfCareRef={patientEpisodesOfCareData}
          isEditMode={isEditMode}
        />
      </ScrollWrapper>
    </>
  )
}

export { PatientDetail }
